/**
 * Revision-related hooks for the Phase 4/5 mindspace surfaces.
 *
 * Wraps `mindspaceApi` daily-plan + queue + review endpoints with React
 * Query, sharing query keys with `useMindspace.ts` so submitting a
 * review invalidates everything that could have been affected
 * (daily-plan, both queues, the concept detail).
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  mindspaceApi,
  type BookmarkResponse,
  type DailyPlanSummary,
  type HighlightsFeedFilters,
  type HighlightsFeedResponse,
  type QueuePayload,
  type ReviewSurface,
  type TagMatchMode,
} from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import { notify } from '@/lib/notify';
// Loaded dynamically, not imported: a static import here put the 1,144-line
// mockPracticeData module in the production bundle. Every use below is already
// inside an async queryFn, so the await is free.
import { loadMockPracticeData } from '@/data';

// Dev-only error surfacing for the silent mock-fallback paths. In
// production we want graceful degradation (the demo keeps working even
// if a backend route is down); in dev we want failures loud so we
// notice broken endpoints instead of debugging mock data.
function devNotifyFallback(label: string, err: unknown) {
  console.warn(`[${label}] API failed, falling back to mock`, err);
  if (import.meta.env.DEV) {
    notify.error(err, `${label}: API failed (dev)`);
  }
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const revisionKeys = {
  // ['mindspace', 'revision'] — NOT ['mindspace'].
  //
  // This was `['mindspace']`, byte-identical to `mindspaceKeys.all` in
  // useMindspace.ts and to `queryKeys.mindspace.all` in config/query-client.ts:
  // three factories, three files, one namespace, no cross-reference. React
  // Query matches keys by prefix, so `invalidateQueries({ queryKey:
  // revisionKeys.all })` in `useRefreshDailyPlan` (:150) matched *everything*
  // any of the three owned — the knowledge graph, every tag-option list, every
  // concept detail. Refreshing the daily-plan card silently refetched the whole
  // Mindspace, and nobody editing either file could have seen it.
  //
  // Nesting under 'mindspace' is deliberate: a genuine whole-domain
  // invalidation via `mindspaceKeys.all` still reaches revision data, but
  // revision's own invalidations no longer reach outward.
  all: ['mindspace', 'revision'] as const,
  dailyPlan: () => [...revisionKeys.all, 'daily-plan'] as const,
  queue: (
    surface: ReviewSurface,
    filters: {
      domain?: string[];
      user?: string[];
      match?: TagMatchMode;
      limit?: number;
      questionsPerConcept?: number;
      lessons?: string[];
      conceptIds?: string[];
      bookmarked?: boolean;
      includeNotDue?: boolean;
      fresh?: boolean;
    } = {}
  ) =>
    [
      ...revisionKeys.all,
      surface,
      'queue',
      {
        domain: filters.domain?.slice().sort() ?? [],
        user: filters.user?.slice().sort() ?? [],
        match: filters.match ?? 'any',
        limit: filters.limit ?? null,
        questionsPerConcept: filters.questionsPerConcept ?? null,
        lessons: filters.lessons?.slice().sort() ?? [],
        conceptIds: filters.conceptIds?.slice().sort() ?? [],
        bookmarked: filters.bookmarked ?? false,
        includeNotDue: filters.includeNotDue ?? false,
        fresh: filters.fresh ?? false,
      },
    ] as const,
  highlightsFeed: (filters: HighlightsFeedFilters | undefined) =>
    [...revisionKeys.all, 'quick-bytes', 'highlights', filters ?? {}] as const,
  // Mutation key for FSRS review submissions. Callers can await these
  // settling (via `qc.isMutating({ mutationKey })`) before recomputing the
  // daily plan, so a fire-and-forget review write isn't raced by the
  // "Due today" refetch.
  submitReview: () => [...revisionKeys.all, 'submit-review'] as const,
};

// ============================================================================
// DAILY PLAN
// ============================================================================

export function useDailyPlan() {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<DailyPlanSummary>({
    queryKey: revisionKeys.dailyPlan(),
    queryFn: async () => {
      if (isEmpty) {
        return {
          computed_at: new Date().toISOString(),
          buckets: {
            introduce: 0,
            must_practice: 0,
            should_practice: 0,
            passive_review: 0,
            overflow: 0,
          },
          summary: {
            practice: { count: 0, estimated_minutes: 0 },
            quick_bytes: { count: 0, estimated_minutes: 0 },
            introduce: { count: 0 },
          },
          starter_packs: {
            due: { count: 0, preview: [] },
            weak: { count: 0, preview: [] },
            starred: { count: 0, preview: [] },
            recent: {
              count: 0,
              preview: [],
              lesson_id: null,
              lesson_title: null,
            },
          },
        };
      }
      if (isMock) return (await loadMockPracticeData()).MOCK_DAILY_PLAN;
      try {
        return await mindspaceApi.getDailyPlan();
      } catch (err) {
        // Match the `useConceptDetail` pattern - fall back to mock so
        // dev environments without a live backend still show *something*
        // useful instead of an empty Practice Arena.
        devNotifyFallback('useDailyPlan', err);
        return (await loadMockPracticeData()).MOCK_DAILY_PLAN;
      }
    },
    staleTime: 4 * 60 * 60 * 1000, // matches backend cache TTL
  });
}

export function useRefreshDailyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => mindspaceApi.refreshDailyPlan(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: revisionKeys.all });
    },
  });
}

// ============================================================================
// QUEUES
// ============================================================================

interface QueueArgs {
  domain?: string[];
  user?: string[];
  match?: TagMatchMode;
  limit?: number;
  enabled?: boolean;
  questionsPerConcept?: number;
  /** Source-lesson allow-list. Restricts the queue to concepts
   *  whose `source_lessons` intersects this list. Honoured by both
   *  the real API (`lessons` repeatable query param) and mock mode. */
  lessons?: string[];
  /** Targeted concept-id allow-list. Mock mode honors it directly;
   *  the real API doesn't accept it yet (backend follow-up), so it's
   *  passed through but the runner also filters client-side. */
  conceptIds?: string[];
  /** Restrict to bookmarked (starred) concepts only. Honoured by both
   *  the real API and mock mode (mock filters via concept.is_bookmarked). */
  bookmarked?: boolean;
  /** Include not-currently-due concepts. "Drill this collection" scopes
   *  (Custom / Recent lesson / Starred) set this so they practice every
   *  match; due-only scopes (Due today / Weak) leave it off. */
  includeNotDue?: boolean;
  /** Bypass the question cache to regenerate fresh quiz questions (repeat). */
  fresh?: boolean;
}

function emptyQueue(surface: ReviewSurface): QueuePayload {
  return { surface, mode: 'auto', count: 0, total: 0, concepts: [] };
}

export function useQuickBytesQueue(args: QueueArgs = {}) {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<QueuePayload>({
    queryKey: revisionKeys.queue('quick_bytes', args),
    queryFn: async () => {
      if (isEmpty) return emptyQueue('quick_bytes');
      if (isMock)
        return (await loadMockPracticeData()).buildMockQueue(
          'quick_bytes',
          args
        );
      try {
        return await mindspaceApi.getQuickBytesQueue({
          domain: args.domain,
          user: args.user,
          match: args.match,
          limit: args.limit,
        });
      } catch (err) {
        devNotifyFallback('useQuickBytesQueue', err);
        return (await loadMockPracticeData()).buildMockQueue(
          'quick_bytes',
          args
        );
      }
    },
    enabled: args.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

const HIGHLIGHTS_PAGE_SIZE = 60;

/**
 * Cross-lesson highlights feed for the Quick Bytes microlearning surface.
 *
 * Uses `useInfiniteQuery` so the bento grid can lazy-load carousels as
 * the user scrolls. `getNextPageParam` returns the next `offset` when
 * the response indicates more rows remain (`offset + count < total`).
 * `empty` data mode returns nothing; `mock` mode honors offset/limit
 * via the in-memory mock builder so pagination is testable without a
 * backend.
 */
export function useQuickBytesHighlights(
  filters?: Omit<HighlightsFeedFilters, 'offset' | 'limit'>,
  options: { enabled?: boolean } = {}
) {
  const { isMock, isEmpty } = useDataMode();
  return useInfiniteQuery<HighlightsFeedResponse, Error>({
    queryKey: revisionKeys.highlightsFeed(filters),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === 'number' ? pageParam : 0;
      const pageFilters: HighlightsFeedFilters = {
        ...filters,
        limit: HIGHLIGHTS_PAGE_SIZE,
        offset,
      };
      if (isEmpty) return { count: 0, total: 0, carousels: [] };
      if (isMock)
        return (await loadMockPracticeData()).buildMockHighlightsFeed(
          pageFilters
        );
      try {
        return await mindspaceApi.getQuickBytesHighlights(pageFilters);
      } catch (err) {
        devNotifyFallback('useQuickBytesHighlights', err);
        return (await loadMockPracticeData()).buildMockHighlightsFeed(
          pageFilters
        );
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.count, 0);
      if (loaded >= lastPage.total) return undefined;
      return loaded;
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Toggle a bookmark on a concept. Optimistic - flips the cached
 * `is_bookmarked` flag across every loaded highlights page so the UI
 * updates instantly; on settle, refetches the feed so the bookmarked
 * filter (if active) re-evaluates.
 */
export function useToggleBookmark() {
  const qc = useQueryClient();
  const { isMock, isEmpty } = useDataMode();
  return useMutation<
    BookmarkResponse,
    Error,
    { conceptId: string; nextBookmarked: boolean },
    { previousPages: unknown }
  >({
    mutationFn: async ({ conceptId, nextBookmarked }) => {
      if (isEmpty || isMock) {
        return { concept_id: conceptId, is_bookmarked: nextBookmarked };
      }
      return nextBookmarked
        ? mindspaceApi.addBookmark(conceptId)
        : mindspaceApi.removeBookmark(conceptId);
    },
    onMutate: async ({ conceptId, nextBookmarked }) => {
      // Cancel in-flight feed + queue queries so they don't clobber our
      // optimistic update with stale data.
      await qc.cancelQueries({
        queryKey: [...revisionKeys.all, 'quick-bytes', 'highlights'],
      });
      await qc.cancelQueries({
        queryKey: [...revisionKeys.all, 'practice', 'queue'],
      });
      // Snapshot both surfaces so we can roll back on error. Keys vary
      // by filter shape so the rollback iterates every matching entry.
      const snapshot = [
        ...qc.getQueriesData({
          queryKey: [...revisionKeys.all, 'quick-bytes', 'highlights'],
        }),
        ...qc.getQueriesData({
          queryKey: [...revisionKeys.all, 'practice', 'queue'],
        }),
      ];
      qc.setQueriesData<{
        pages: HighlightsFeedResponse[];
        pageParams: unknown[];
      }>(
        { queryKey: [...revisionKeys.all, 'quick-bytes', 'highlights'] },
        old => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              carousels: page.carousels.map(c =>
                c.concept_id === conceptId
                  ? { ...c, is_bookmarked: nextBookmarked }
                  : c
              ),
            })),
          };
        }
      );
      // Patch the practice-queue cache so an in-progress runner sees
      // the new starred state immediately (the star button reads
      // `concept.is_bookmarked` from the queue payload).
      qc.setQueriesData<QueuePayload>(
        { queryKey: [...revisionKeys.all, 'practice', 'queue'] },
        old => {
          if (!old) return old;
          return {
            ...old,
            concepts: old.concepts.map(c =>
              c.concept_id === conceptId
                ? { ...c, is_bookmarked: nextBookmarked }
                : c
            ),
          };
        }
      );
      return { previousPages: snapshot };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back to whatever we had before the optimistic update.
      const snapshot = ctx?.previousPages as
        | Array<[unknown, unknown]>
        | undefined;
      if (!snapshot) return;
      for (const [key, data] of snapshot) {
        qc.setQueryData(key as readonly unknown[], data);
      }
    },
    onSettled: () => {
      // Refetch so the bookmarked-only filter view re-evaluates
      // membership and the canonical state takes over from the
      // optimistic patch. Practice-queue cache is left alone - the
      // user is likely mid-session and we don't want to refetch
      // questions just because they starred a concept.
      if (!isMock && !isEmpty) {
        qc.invalidateQueries({
          queryKey: [...revisionKeys.all, 'quick-bytes', 'highlights'],
        });
      }
    },
  });
}

export function usePracticeQueue(args: QueueArgs = {}) {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<QueuePayload>({
    queryKey: revisionKeys.queue('practice', args),
    // RQ injects an AbortSignal that fires when this query becomes
    // stale (key change, component unmount, refetch). Threading it
    // through to fetch() so a count-probe with stale filters doesn't
    // settle after a newer query - the in-flight HTTP request is
    // cancelled the moment its query key is superseded.
    queryFn: async ({ signal }) => {
      if (isEmpty) return emptyQueue('practice');
      if (isMock)
        return (await loadMockPracticeData()).buildMockQueue('practice', args);
      try {
        return await mindspaceApi.getPracticeQueue(
          {
            domain: args.domain,
            user: args.user,
            match: args.match,
            limit: args.limit,
            questions_per_concept: args.questionsPerConcept,
            bookmarked: args.bookmarked,
            lessons: args.lessons,
            concept_ids: args.conceptIds,
            include_not_due: args.includeNotDue,
            refresh: args.fresh,
          },
          { signal }
        );
      } catch (err) {
        // AbortError from a stale-cancel is not a real failure - let RQ
        // discard the result silently rather than firing the dev toast.
        if (signal?.aborted) throw err;
        devNotifyFallback('usePracticeQueue', err);
        return (await loadMockPracticeData()).buildMockQueue('practice', args);
      }
    },
    enabled: args.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

// ============================================================================
// REVIEW + TAGS
// ============================================================================

export function useSubmitReview() {
  const qc = useQueryClient();
  const { isMock, isEmpty } = useDataMode();
  return useMutation({
    // Keyed so the session-finish flow can wait for in-flight review writes
    // to settle (via `qc.isMutating`) before it refetches the daily plan.
    mutationKey: revisionKeys.submitReview(),
    mutationFn: async (vars: {
      conceptId: string;
      grade: 1 | 2 | 3 | 4;
      source: ReviewSurface;
      /** Optional idempotency token (UUID per flashcard view or per quiz
       *  session). Same value re-submitted is deduped server-side. */
      attemptId?: string;
      /** The lesson a not-yet-synced concept comes from: `conceptId` is
       *  then its per-lesson id and the server keeps the grade. */
      lessonId?: string;
    }) => {
      if (isEmpty || isMock) {
        // No persistence in non-API modes - the flashcard runner still
        // advances cleanly, but the cached queue isn't invalidated so
        // the same concept won't disappear mid-session.
        return {
          concept_id: vars.conceptId,
          sr_state: {
            stability: vars.grade,
            difficulty: 5,
            last_reviewed_at: new Date().toISOString(),
            review_count: 1,
            last_grade: vars.grade,
            struggle_score: 0,
          },
        };
      }
      // API mode. Unlike the read hooks, a review is a *write*: silently
      // swallowing a failure and fabricating an sr_state would drop the
      // user's review server-side while the UI reports success. Rethrow so
      // React Query's `retry` absorbs transient failures and, if they
      // persist, the mutation lands in its error state and the caller's
      // onError surfaces it (rather than a fabricated success).
      try {
        return await mindspaceApi.submitReview(vars.conceptId, {
          grade: vars.grade,
          source: vars.source,
          attempt_id: vars.attemptId,
          lesson_id: vars.lessonId,
        });
      } catch (err) {
        devNotifyFallback('useSubmitReview', err);
        throw err;
      }
    },
    // Transient network blips shouldn't lose a review - the attempt_id makes
    // the submit idempotent server-side, so retrying is safe.
    retry: 2,
    onSuccess: () => {
      // Mark mindspace data stale, but with `refetchType: 'none'` so we do
      // NOT refetch the *active* practice queue mid-session - refetching it
      // would return the now-smaller due set and churn the runner on every
      // rating. The session deck is frozen anyway; these caches refresh on
      // their next mount (e.g. the setup screen after finishSession's own
      // invalidate). Mock/empty modes skip it so the deck never reshuffles.
      if (!isMock && !isEmpty) {
        qc.invalidateQueries({
          queryKey: revisionKeys.all,
          refetchType: 'none',
        });
      }
    },
  });
}

export function usePatchUserTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      conceptId: string;
      add?: string[];
      remove?: string[];
    }) =>
      mindspaceApi.patchUserTags(vars.conceptId, {
        add: vars.add,
        remove: vars.remove,
      }),
    onSuccess: () => {
      // `revisionKeys.all` is `['mindspace']` - same prefix as the
      // concept detail query and the archive graph query - so one
      // invalidation refreshes the sidebar tags, the daily plan, and
      // the graph payload's domain/user tag options together.
      qc.invalidateQueries({ queryKey: revisionKeys.all });
    },
  });
}
